import React, { useMemo, useRef, useState } from 'react';
import { t } from '../../onboarding/strings';
import { metaForField, REQUIRED_SET } from '../../onboarding/catalog';
import './JsonEditor.css';

// Left-border accent per palette category, so blocks are scannable.
const CATEGORY_COLORS = {
  'Profile basics': '#6366f1',
  'Body & metrics': '#0ea5e9',
  'Goals & preferences': '#10b981',
  'Aha & charts': '#f59e0b',
  'Social proof': '#ec4899',
  'System & paywall': '#8b5cf6',
};

/* ---- syntax highlighting (no deps) ---------------------------------- */

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function highlight(json) {
  return escapeHtml(json).replace(
    /("(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'num';
      if (match.startsWith('"')) cls = match.endsWith(':') ? 'key' : 'str';
      else if (match === 'true' || match === 'false') cls = 'bool';
      else if (match === 'null') cls = 'null';
      return `<span class="jt-${cls}">${match}</span>`;
    }
  );
}

/* ---- editable code area with highlight overlay ----------------------- */

function CodeArea({ value, onChange }) {
  const preRef = useRef(null);
  const html = useMemo(() => highlight(value) + '\n', [value]);
  const lines = value.split('\n').length;
  const height = Math.min(Math.max(lines, 3) * 18 + 22, 460);

  function onKeyDown(e) {
    if (e.key === 'Tab') {
      e.preventDefault();
      const el = e.target;
      const s = el.selectionStart;
      const en = el.selectionEnd;
      onChange(value.slice(0, s) + '  ' + value.slice(en));
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = s + 2;
      });
    }
  }

  return (
    <div className="je-code" style={{ height }}>
      <pre ref={preRef} className="je-hl" dangerouslySetInnerHTML={{ __html: html }} />
      <textarea
        className="je-ta"
        value={value}
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onScroll={(e) => {
          if (preRef.current) {
            preRef.current.scrollTop = e.target.scrollTop;
            preRef.current.scrollLeft = e.target.scrollLeft;
          }
        }}
      />
    </div>
  );
}

/* ---- per-block validation -------------------------------------------- */

function validateBlock(text) {
  let page;
  try {
    page = JSON.parse(text);
  } catch (e) {
    return { ok: false, msg: e.message };
  }
  if (!page || typeof page !== 'object' || Array.isArray(page)) {
    return { ok: false, msg: 'Each block must be a single page object.' };
  }
  if (!page.field || !page.content || !page.content.type) {
    return { ok: false, msg: 'Missing "field" or "content.type".' };
  }
  return { ok: true, page };
}

const NEW_PAGE_TEMPLATE = `{
  "title": "onboarding_..._title",
  "field": "",
  "content": {
    "type": ""
  }
}`;

/* ---- modal ------------------------------------------------------------ */

let BID = 1;

export default function JsonEditorModal({ versionName, items, onApply, onClose }) {
  const [blocks, setBlocks] = useState(() =>
    items.map((it) => ({
      id: 'b' + BID++,
      text: JSON.stringify(it.page, null, 2),
      open: false,
      enabled: it.enabled,
    }))
  );
  const [copied, setCopied] = useState(false);

  const results = useMemo(() => blocks.map((b) => validateBlock(b.text)), [blocks]);
  const invalidCount = results.filter((r) => !r.ok).length;
  const allOk = invalidCount === 0;

  const patch = (id, changes) =>
    setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...changes } : b)));

  function setAllOpen(open) {
    setBlocks((bs) => bs.map((b) => ({ ...b, open })));
  }
  function formatBlock(id) {
    setBlocks((bs) =>
      bs.map((b) => {
        if (b.id !== id) return b;
        try {
          return { ...b, text: JSON.stringify(JSON.parse(b.text), null, 2) };
        } catch (_) {
          return b;
        }
      })
    );
  }
  function deleteBlock(id) {
    const idx = blocks.findIndex((b) => b.id === id);
    const r = results[idx];
    if (r && r.ok && REQUIRED_SET.has(r.page.field)) {
      const count = blocks.filter((b, i) => results[i].ok && results[i].page.field === r.page.field).length;
      if (count <= 1) {
        window.alert(`"${r.page.field}" is a required page — it can be moved, but not deleted.`);
        return;
      }
    }
    setBlocks((bs) => bs.filter((b) => b.id !== id));
  }
  function addBlock() {
    const b = { id: 'b' + BID++, text: NEW_PAGE_TEMPLATE, open: true, enabled: true };
    setBlocks((bs) => bs.concat(b));
  }
  function copyAll() {
    if (!allOk) return;
    const json = JSON.stringify(results.map((r) => r.page), null, 2);
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  function apply() {
    if (!allOk) return;
    onApply(blocks.map((b, i) => ({ page: results[i].page, enabled: b.enabled })));
  }

  return (
    <div className="ob-modal-overlay" onClick={onClose}>
      <div className="je-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ob-modal-head">
          <h3>{'{ }'} JSON — “{versionName}”</h3>
          <button className="ob-modal-x" onClick={onClose}>✕</button>
        </div>

        <div className="je-toolbar">
          <button className="je-tool" onClick={() => setAllOpen(true)}>⊞ Expand all</button>
          <button className="je-tool" onClick={() => setAllOpen(false)}>⊟ Collapse all</button>
          <button className="je-tool" onClick={addBlock}>＋ Add page</button>
          <span className={`je-status ${allOk ? 'ok' : 'err'}`}>
            {allOk
              ? `✓ ${blocks.length} page(s) · all valid`
              : `✗ ${invalidCount} invalid block(s)`}
          </span>
        </div>

        <div className="je-blocks">
          {blocks.length === 0 && <div className="je-empty">No pages. “＋ Add page” to start.</div>}
          {blocks.map((b, i) => {
            const r = results[i];
            const page = r.ok ? r.page : null;
            const meta = page ? metaForField(page.field) : null;
            const color = meta ? CATEGORY_COLORS[meta.category] || '#9ca3af' : '#ef4444';
            const title = page ? t(page.title) || meta.label : 'Invalid JSON';
            return (
              <div key={b.id} className="je-block" style={{ borderLeftColor: color }}>
                <div className="je-bhead" onClick={() => patch(b.id, { open: !b.open })}>
                  <span className="je-chev">{b.open ? '▾' : '▸'}</span>
                  <span className="je-idx">{i + 1}</span>
                  <span className="je-emoji">{meta ? meta.emoji : '⚠️'}</span>
                  <span className="je-btitle">{title}</span>
                  {page && REQUIRED_SET.has(page.field) && (
                    <span className="je-req" title="Required page — can be moved, not deleted">🔒</span>
                  )}
                  {page && <code className="je-bfield">{page.field}</code>}
                  {page && page.content && <code className="je-btype">{page.content.type}</code>}
                  {!b.enabled && <span className="je-off">off</span>}
                  <span className={`je-dot ${r.ok ? 'ok' : 'err'}`} title={r.ok ? 'Valid' : r.msg} />
                  <button
                    className="je-bbtn"
                    title="Format block"
                    onClick={(e) => { e.stopPropagation(); formatBlock(b.id); }}
                  >⟲</button>
                  <button
                    className="je-bbtn danger"
                    title="Delete page"
                    onClick={(e) => { e.stopPropagation(); deleteBlock(b.id); }}
                  >✕</button>
                </div>
                {b.open && (
                  <div className="je-bbody">
                    <CodeArea value={b.text} onChange={(text) => patch(b.id, { text })} />
                    {!r.ok && <div className="je-berr">✗ {r.msg}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="ob-modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-secondary" onClick={copyAll} disabled={!allOk}>
            {copied ? '✓ Copied' : '⧉ Copy all'}
          </button>
          <button className="btn btn-primary" onClick={apply} disabled={!allOk}>Apply changes</button>
        </div>
      </div>
    </div>
  );
}
